import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useDashboard } from "../hooks/useDashboard";
import { useRevenue } from "../hooks/useRevenue";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Calendar,
  CheckCircle,
  Lightbulb,
  PhoneCall,
  Plus,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import ConversionChart from "../components/dashboard/ConversionChart";
import LoadingSpinner from "../components/common/LoadingSpinner";
import RevenueSummary from "../components/dashboard/RevenueSummary";

const DashboardPage = () => {
  const { currentUser, isAdmin } = useAuth();
  const { stats, recentLeads, loading } = useDashboard();
  const {
    globalTarget,
    userObjective,
    teamProgress,
    userProgress,
    latestObservation,
    loading: revenueLoading,
    error: revenueError,
  } = useRevenue();

  const conversionRate = Number(stats.conversionRate || 0);
  const qualificationRate = useMemo(() => {
    if (!stats.total) return 0;
    return Number(((stats.qualifie / stats.total) * 100).toFixed(1));
  }, [stats.qualifie, stats.total]);

  const engagementRate = useMemo(() => {
    if (!stats.total) return 0;
    const engaged = stats.converti + stats.negociation + stats.qualifie;
    return Number(((engaged / stats.total) * 100).toFixed(1));
  }, [stats.converti, stats.negociation, stats.qualifie, stats.total]);

  const forecast = useMemo(() => {
    const base = stats.converti || 0;
    const pipeline = stats.negociation || 0;
    return base + Math.round(pipeline * 0.6);
  }, [stats.converti, stats.negociation]);

  const accentStyles = {
    brand: { backgroundColor: "rgba(37,99,235,0.14)", color: "#1d4ed8" },
    success: { backgroundColor: "rgba(18,183,106,0.18)", color: "#0f5132" },
    info: { backgroundColor: "rgba(79,70,229,0.18)", color: "#4338ca" },
    warning: { backgroundColor: "rgba(247,144,9,0.18)", color: "#7c2d12" },
  };

  const kpis = useMemo(
    () => [
      {
        title: "Prospects actifs",
        value: stats.total,
        delta: `+${stats.nouveau || 0} ce mois`,
        icon: Users,
        accent: accentStyles.brand,
        link: "/leads",
      },
      {
        title: "Taux de conversion",
        value: `${conversionRate}%`,
        delta: conversionRate >= 20 ? "Excellent" : "à surveiller",
        icon: CheckCircle,
        accent: accentStyles.success,
        link: "/leads?status=converti",
      },
      {
        title: "Qualification",
        value: `${qualificationRate}%`,
        delta: `${stats.qualifie || 0} qualifiés`,
        icon: Target,
        accent: accentStyles.info,
        link: "/leads?status=qualifié",
      },
      {
        title: "Prévision conversions",
        value: forecast,
        delta: `${stats.negociation || 0} en négociation`,
        icon: TrendingUp,
        accent: accentStyles.warning,
        link: "/leads?status=en_negociation",
      },
    ],
    [stats, conversionRate, qualificationRate, forecast]
  );

  const pipelinePalette = {
    nouveau: { backgroundColor: "rgba(37,99,235,0.16)", color: "#1d4ed8" },
    contacte: { backgroundColor: "rgba(79,70,229,0.16)", color: "#4338ca" },
    qualifie: { backgroundColor: "rgba(6,182,212,0.16)", color: "#0e7490" },
    negociation: { backgroundColor: "rgba(247,144,9,0.16)", color: "#b45309" },
    converti: { backgroundColor: "rgba(16,185,129,0.16)", color: "#047857" },
  };

  const pipelineSteps = useMemo(
    () => [
      {
        key: "nouveau",
        label: "Nouveaux",
        value: stats.nouveau || 0,
        palette: pipelinePalette.nouveau,
      },
      {
        key: "contacte",
        label: "Contactés",
        value: stats.contacte || 0,
        palette: pipelinePalette.contacte,
      },
      {
        key: "qualifie",
        label: "Qualifiés",
        value: stats.qualifie || 0,
        palette: pipelinePalette.qualifie,
      },
      {
        key: "negociation",
        label: "Négociation",
        value: stats.negociation || 0,
        palette: pipelinePalette.negociation,
      },
      {
        key: "converti",
        label: "Convertis",
        value: stats.converti || 0,
        palette: pipelinePalette.converti,
      },
    ],
    [stats]
  );

  const actionItems = useMemo(() => {
    const items = [];

    if (stats.nouveau > 0) {
      items.push({
        title: "Relancer vos nouveaux prospects",
        description: `Il reste ${stats.nouveau} prospects sans premier contact. Planifiez vos appels prioritaires aujourd'hui.`,
        icon: PhoneCall,
        link: "/leads?status=nouveau",
        cta: "Voir la liste",
      });
    }

    if (stats.negociation > 0) {
      items.push({
        title: "Securiser les négociations en cours",
        description: `${stats.negociation} opportunites sont en phase critique. Préparez un argumentaire final pour conclure.`,
        icon: Target,
        link: "/leads?status=en_negociation",
        cta: "Préparer",
      });
    }

    if (stats.perdu > 0) {
      items.push({
        title: "Analyser les pertes récentes",
        description: `Identifiez les causes des ${stats.perdu} dossiers perdus pour ajuster vos scripts de prospection.`,
        icon: AlertTriangle,
        link: "/leads?status=perdu",
        cta: "Analyser",
      });
    }

    if (items.length < 3) {
      items.push(
        {
          title: "Planifier une campagne ciblée",
          description:
            "Segmentez vos prospects qualifiés et lancez une campagne multicanale sur les 7 prochains jours.",
          icon: Calendar,
          link: "/leads",
          cta: "Planifier",
        },
        {
          title: "Documenter vos parcours",
          description:
            "Ajoutez des notes détaillées après chaque interaction pour garder une visibilité parfaite sur le pipeline.",
          icon: Lightbulb,
          link: "/leads",
          cta: "Mettre à jour",
        }
      );
    }

    return items.slice(0, 3);
  }, [stats]);

  const greeting = useMemo(() => {
    const name =
      currentUser?.displayName || currentUser?.email?.split("@")[0] || "Invité";
    const date = new Date().toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return { name, date };
  }, [currentUser]);

  if (loading || revenueLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-[var(--color-text-secondary)] font-medium">
            Chargement de votre tableau de bord...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-page)]">
      <main className="container-custom py-10 space-y-8">
        <section className="panel-surface relative overflow-hidden p-8">
          <div className="absolute inset-y-0 right-0 w-1/3 bg-[var(--color-brand-soft)] blur-3xl opacity-60 pointer-events-none" />
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-3 max-w-xl">
              <p className="text-sm uppercase tracking-wide text-[var(--color-text-muted)]">
                {isAdmin ? "Vue direction" : "Vue commerciale"}
              </p>
              <h1 className="text-3xl font-semibold text-[var(--color-text-primary)]">
                Bonjour {greeting.name}, voici la santé de votre pipeline.
              </h1>
              <p className="text-[var(--color-text-secondary)]">
                {greeting.date}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <Link to="/leads/add" className="btn btn-primary">
                <Plus className="w-4 h-4" />
                Nouveau prospect
              </Link>
              <Link to="/leads" className="btn btn-outline">
                Gérer mes prospects
              </Link>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map(({ title, value, delta, icon: Icon, accent, link }) => (
              <Link
                key={title}
                to={link}
                className="panel-quiet p-5 hover:shadow-soft-lg transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-soft"
                    style={accent}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[var(--color-text-muted)]" />
                </div>
                <p className="mt-6 text-sm font-medium text-[var(--color-text-muted)]">
                  {title}
                </p>
                <p className="text-2xl font-semibold text-[var(--color-text-primary)]">
                  {value}
                </p>
                <p className="text-xs font-medium text-[var(--color-text-secondary)] mt-2">
                  {delta}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {revenueError && (
          <section className="panel-quiet p-4 border border-red-200 bg-red-50 text-red-700">
            Impossible de charger le suivi du chiffre d'affaires. Veuillez
            réessayer plus tard.
          </section>
        )}

        <RevenueSummary
          isAdmin={isAdmin}
          globalTarget={globalTarget}
          userObjective={userObjective}
          progress={userProgress}
          teamProgress={teamProgress}
          latestObservation={latestObservation}
        />

        <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <div className="panel-surface p-6">
            <ConversionChart data={stats} />
          </div>
          <div className="panel-surface p-6 flex flex-col gap-6">
            <header className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                Centre d'actions
              </h2>
              <Activity className="w-5 h-5 text-[var(--color-brand)]" />
            </header>
            <ul className="space-y-4">
              {actionItems.map(
                ({ title, description, icon: Icon, link, cta }) => (
                  <li
                    key={title}
                    className="panel-quiet p-4 hover:shadow-soft-lg transition-all duration-200"
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 w-10 h-10 rounded-xl bg-[var(--color-brand-soft)] text-[var(--color-brand)] flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                          {title}
                        </h3>
                        <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                          {description}
                        </p>
                        <Link
                          to={link}
                          className="text-sm font-medium text-[var(--color-brand)] hover:text-[var(--color-brand-strong)] inline-flex items-center gap-1"
                        >
                          {cta} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </li>
                )
              )}
            </ul>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.4fr,1fr]">
          <div className="panel-surface p-6">
            <header className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                  Analyse des performances
                </h2>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Suivi des indicateurs clés du cycle de vente.
                </p>
              </div>
              <Lightbulb className="w-5 h-5 text-[var(--color-brand)]" />
            </header>

            <div className="space-y-6">
              <ProgressBar
                label="Conversion globale"
                value={conversionRate}
                tone="from-emerald-400 to-emerald-600"
                helper={`${stats.converti || 0} deals signés`}
              />
              <ProgressBar
                label="Qualification"
                value={qualificationRate}
                tone="from-indigo-400 to-indigo-600"
                helper={`${stats.qualifie || 0} prospects éligibles`}
              />
              <ProgressBar
                label="Engagement pipeline"
                value={engagementRate}
                tone="from-sky-400 to-sky-600"
                helper={`${stats.negociation || 0} négociations actives`}
              />
            </div>
          </div>

          <div className="panel-surface p-6">
            <header className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                Pipeline
              </h2>
              <span className="text-sm font-medium text-[var(--color-text-muted)]">
                {stats.total} prospects
              </span>
            </header>
            <ul className="space-y-3">
              {pipelineSteps.map((step) => (
                <li key={step.key} className="panel-quiet p-4">
                  <div className="flex items-center justify-between">
                    <span
                      className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full"
                      style={step.palette}
                    >
                      {step.label}
                    </span>
                    <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                      {step.value}
                    </span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-[var(--color-bg-surface)] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[var(--color-brand)]/70"
                      style={{
                        width: `${
                          stats.total
                            ? Math.min(100, (step.value / stats.total) * 100)
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="panel-surface p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                Prospects récents
              </h2>
              <p className="text-sm text-[var(--color-text-muted)]">
                Suivez les dernières entrées dans votre CRM.
              </p>
            </div>
            <Link to="/leads" className="btn btn-outline">
              Voir tous
            </Link>
          </div>

          {recentLeads.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[var(--color-text-muted)]">
                    <th className="py-2 font-medium">Prospect</th>
                    <th className="py-2 font-medium">Entreprise</th>
                    <th className="py-2 font-medium">Statut</th>
                    <th className="py-2 font-medium">Contact</th>
                    <th className="py-2 text-right font-medium">Détails</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border-soft)]/70">
                  {recentLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-[var(--color-bg-surface)]"
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white font-semibold flex items-center justify-center">
                            {lead.firstName?.[0] || ""}
                            {lead.lastName?.[0] || ""}
                          </div>
                          <div>
                            <Link
                              to={`/leads/${lead.id}`}
                              className="font-medium text-[var(--color-text-primary)] hover:text-[var(--color-brand)]"
                            >
                              {lead.firstName} {lead.lastName}
                            </Link>
                            <p className="text-xs text-[var(--color-text-muted)]">
                              {lead.jobTitle || "N/A"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-[var(--color-text-secondary)]">
                        {lead.company || "N/A"}
                      </td>
                      <td className="py-3">
                        <span className="badge badge-info">{lead.status}</span>
                      </td>
                      <td className="py-3">
                        <div className="space-y-1 text-xs text-[var(--color-text-secondary)]">
                          <p className="font-medium">{lead.phone || "N/A"}</p>
                          <p>{lead.email || "N/A"}</p>
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="text-[var(--color-brand)] hover:text-[var(--color-brand-strong)] inline-flex items-center gap-1 text-xs font-semibold"
                        >
                          Voir <ArrowUpRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const ProgressBar = ({ label, value, tone, helper }) => {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
          {label}
        </span>
        <span className="text-sm font-semibold text-[var(--color-text-primary)]">
          {clamped}%
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-[var(--color-bg-surface)]">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${tone}`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {helper && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">{helper}</p>
      )}
    </div>
  );
};

const EmptyState = () => (
  <div className="panel-quiet p-10 text-center">
    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-brand-soft)] text-[var(--color-brand)]">
      <Users className="w-8 h-8" />
    </div>
    <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
      Aucun prospect récent
    </h3>
    <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
      Ajoutez un prospect pour alimenter votre pipeline et suivre vos
      opportunités.
    </p>
    <Link to="/leads/add" className="mt-6 inline-flex btn btn-primary">
      <Plus className="w-4 h-4" />
      Ajouter un prospect
    </Link>
  </div>
);

export default DashboardPage;
